import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { label, when } from "@/lib/trust";

const Stat = ({ k, v, testid }) => (
  <div className="rounded border border-[#2A3447] bg-[#0E1424] p-3" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label(k)}</p>
    <p className="font-heading text-2xl text-amber-100">{v ?? "—"}</p>
  </div>
);

function Block({ title, data, testid }) {
  return (
    <div className="panel space-y-3 p-5" data-testid={testid}>
      <p className="eyebrow">{title}</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{Object.entries(data).map(([k, v]) => typeof v === "object" && v !== null
        ? Object.entries(v).map(([k2, v2]) => <Stat key={k + k2} k={`${k} ${k2}`} v={v2} testid={`${testid}-${k}-${k2}`} />)
        : <Stat key={k} k={k} v={v} testid={`${testid}-${k}`} />)}</div>
    </div>
  );
}

function Announcements() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["staff", "announcements"], queryFn: () => api.get("/staff/announcements").then((r) => r.data.items) });
  const [f, setF] = useState({ title: "", body: "", audience: "staff" });
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); qc.invalidateQueries({ queryKey: ["staff", "announcements"] }); qc.invalidateQueries({ queryKey: ["site-announcements"] }); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <div className="panel space-y-3 p-5" data-testid="staff-announcements">
      <p className="eyebrow">Administrative announcements</p>
      {data.map((a) => (
        <div key={a.announcement_id} className="flex items-start justify-between gap-3 border-t border-[#2A3447] pt-3 text-sm first:border-0 first:pt-0" data-testid={`staff-announcement-${a.announcement_id}`}>
          <div><p className="text-amber-100">{a.title} <span className="font-mono text-[10px] uppercase text-aether-300">{a.audience}</span></p><p className="text-slate-400">{a.body}</p><p className="text-[11px] text-slate-600">{when(a.created_at)}</p></div>
          {can("announcements.manage") && <button className="btn-ghost text-xs" onClick={() => run(() => api.delete(`/staff/announcements/${a.announcement_id}`), "Announcement retired")} data-testid={`staff-announcement-retire-${a.announcement_id}`}>Retire</button>}
        </div>
      ))}
      {!data.length && <p className="text-sm text-slate-500">No active announcements.</p>}
      {can("announcements.manage") && <div className="grid gap-2 border-t border-[#2A3447] pt-3 sm:grid-cols-[1fr_1fr_auto_auto]">
        <input className="field" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="announcement-title-input" />
        <input className="field" placeholder="Message" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="announcement-body-input" />
        <select className="field" value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })} data-testid="announcement-audience-select"><option value="staff">Staff only</option><option value="everyone">Site-wide banner</option></select>
        <button className="btn-gold" onClick={() => run(() => api.post("/staff/announcements", f).then(() => setF({ title: "", body: "", audience: "staff" })), "Announcement posted")} data-testid="announcement-post-button">Post</button>
      </div>}
    </div>
  );
}

export function Overview() {
  const { data } = useQuery({ queryKey: ["staff", "summary"], queryFn: () => api.get("/staff/summary").then((r) => r.data) });
  return (
    <div className="space-y-5" data-testid="staff-overview">
      {data?.applications && <Block title="Application reviews" data={data.applications} testid="summary-applications" />}
      {data?.moderation && <Block title="Open moderation cases" data={data.moderation} testid="summary-moderation" />}
      {data?.appeals && <Block title="Appeals awaiting decisions" data={data.appeals} testid="summary-appeals" />}
      {data?.verification && <Block title="Verification" data={data.verification} testid="summary-verification" />}
      {data?.workload && <div className="panel overflow-x-auto p-5" data-testid="summary-workload"><p className="eyebrow mb-3">Staff workload</p>
        <table className="w-full text-left text-sm"><thead className="font-mono text-[10px] uppercase tracking-widest text-amber-300/80"><tr><th className="py-2">Staff</th><th>Role</th><th>Applications</th><th>Cases</th><th>Appeals</th><th>Spot checks</th></tr></thead>
          <tbody>{data.workload.map((w) => <tr key={w.username} className="border-t border-[#2A3447]"><td className="py-2">@{w.username}</td><td>{label(w.role)}</td><td>{w.applications}</td><td>{w.cases}</td><td>{w.appeals}</td><td>{w.spot_checks}</td></tr>)}</tbody></table></div>}
      <Announcements />
    </div>
  );
}
